import {
  buildResultItem,
  fetchSearchResults,
  getPagefind,
  preloadSearch,
} from './search-core.js';

const root = document.getElementById('header-search');
if (!root) {
  // Not in layout
} else {
  const toggle = document.getElementById('header-search-toggle');
  const panel = document.getElementById('header-search-panel');
  const input = document.getElementById('header-search-input');
  const resultsEl = document.getElementById('header-search-results');
  const statusEl = document.getElementById('header-search-status');
  const moreLink = document.getElementById('header-search-more');
  const backdrop = document.getElementById('header-search-backdrop');

  let activeIndex = -1;

  function isOpen() {
    return !panel.hidden;
  }

  function setOpen(open) {
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    input.setAttribute('aria-expanded', String(open));
    if (backdrop) backdrop.hidden = !open;
    root.classList.toggle('header-search--open', open);
    if (open) {
      input.focus();
      getPagefind((msg) => {
        statusEl.textContent = msg;
      });
    } else {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
    }
  }

  function updateMoreLink(q) {
    const trimmed = q.trim();
    moreLink.href = trimmed ? `/search/?q=${encodeURIComponent(trimmed)}` : '/search/';
  }

  function renderResults(items) {
    resultsEl.replaceChildren();
    const frag = document.createDocumentFragment();
    items.forEach((item, i) => {
      const li = buildResultItem(item, { compact: true });
      li.id = `header-search-result-${i}`;
      li.setAttribute('role', 'option');
      const link = li.querySelector('a');
      if (link) {
        link.id = `header-search-result-link-${i}`;
        link.tabIndex = -1;
      }
      frag.append(li);
    });
    resultsEl.append(frag);
    activeIndex = -1;
  }

  function setStatus(total, q) {
    const trimmed = q.trim();
    updateMoreLink(trimmed);
    if (!trimmed) {
      statusEl.textContent = '';
      resultsEl.replaceChildren();
      return;
    }
    if (total === 0) {
      statusEl.textContent = 'No results';
    } else if (total > 8) {
      statusEl.textContent = `${total} results — see all below`;
    } else {
      statusEl.textContent = total === 1 ? '1 result' : `${total} results`;
    }
  }

  function highlightOption(index) {
    const options = resultsEl.querySelectorAll('[role="option"]');
    options.forEach((el, i) => {
      el.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    if (index >= 0 && options[index]) {
      const link = options[index].querySelector('a');
      input.setAttribute('aria-activedescendant', link?.id ?? '');
      options[index].scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
    activeIndex = index;
  }

  async function runSearch(term) {
    const q = term.trim();
    if (!q) {
      setStatus(0, q);
      return;
    }

    statusEl.textContent = 'Searching…';
    const result = await fetchSearchResults(q, {
      limit: 8,
      onError: (msg) => {
        statusEl.textContent = msg;
      },
    });
    if (result === null) return;

    renderResults(result.data);
    setStatus(result.total, q);
  }

  toggle.addEventListener('click', () => {
    setOpen(!isOpen());
  });

  backdrop?.addEventListener('click', () => setOpen(false));

  input.addEventListener('input', (e) => {
    const { value } = e.target;
    preloadSearch(value);
    runSearch(value);
  });

  input.addEventListener('keydown', (e) => {
    const options = resultsEl.querySelectorAll('[role="option"]');
    if (e.key === 'Escape') {
      setOpen(false);
      toggle.focus();
      return;
    }
    if (!options.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlightOption(Math.min(activeIndex + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlightOption(Math.max(activeIndex - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      options[activeIndex].querySelector('a')?.click();
    }
  });

  resultsEl.addEventListener('click', (e) => {
    if (e.target.closest('a')) setOpen(false);
  });

  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setOpen(true);
    }
  });

  document.addEventListener('click', (e) => {
    if (!isOpen()) return;
    if (!root.contains(e.target)) setOpen(false);
  });
}
